import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/material';

interface Ray {
  x: number;
  y: number;
  length: number;
  angle: number;
  speed: number;
  opacity: number;
  width: number;
}

const AnimatedBackground: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>(0);
  const raysRef = useRef<Ray[]>([]);

  // Initialize rays
  const initRays = (width: number, height: number) => {
    const rays: Ray[] = [];
    const rayCount = 30; // Increased ray count
    
    for (let i = 0; i < rayCount; i++) {
      rays.push({
        x: Math.random() * width,
        y: Math.random() * height,
        length: Math.random() * 300 + 200, // Longer rays
        angle: Math.random() * Math.PI * 2,
        speed: 0.1 + Math.random() * 0.2, // Slightly slower for more subtle movement
        opacity: 0.1 + Math.random() * 0.2, // Increased opacity
        width: 2 + Math.random() * 4 // Wider rays
      });
    }
    
    raysRef.current = rays;
  };

  // Update rays positions
  const updateRays = (width: number, height: number) => {
    raysRef.current.forEach(ray => {
      ray.angle += ray.speed * 0.01;
      
      // Ensure rays stay within bounds
      if (ray.x < -ray.length) ray.x = width + ray.length;
      if (ray.x > width + ray.length) ray.x = -ray.length;
      if (ray.y < -ray.length) ray.y = height + ray.length;
      if (ray.y > height + ray.length) ray.y = -ray.length;
      
      // Slowly move rays
      ray.x += Math.cos(ray.angle) * 0.5;
      ray.y += Math.sin(ray.angle) * 0.5;
    });
  };

  // Draw rays
  const drawRays = (ctx: CanvasRenderingContext2D) => {
    ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
    
    // Add a subtle glow effect to the entire canvas
    ctx.fillStyle = 'rgba(102, 126, 234, 0.03)';
    ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);
    
    // Enable global composite operation for glow effect
    ctx.globalCompositeOperation = 'lighter';
    
    raysRef.current.forEach(ray => {
      ctx.beginPath();
      ctx.moveTo(ray.x, ray.y);
      
      const endX = ray.x + Math.cos(ray.angle) * ray.length;
      const endY = ray.y + Math.sin(ray.angle) * ray.length;
      
      ctx.lineTo(endX, endY);
      
      const gradient = ctx.createLinearGradient(ray.x, ray.y, endX, endY);
      gradient.addColorStop(0, `rgba(102, 126, 234, ${ray.opacity})`);
      gradient.addColorStop(0.5, `rgba(118, 75, 162, ${ray.opacity * 0.8})`);
      gradient.addColorStop(1, `rgba(240, 147, 251, ${ray.opacity * 0.4})`);
      
      ctx.strokeStyle = gradient;
      ctx.lineWidth = ray.width;
      ctx.lineCap = 'round';
      ctx.stroke();
      
      // Add a subtle blur/glow to each ray
      ctx.shadowColor = 'rgba(102, 126, 234, 0.3)';
      ctx.shadowBlur = 10;
      ctx.stroke();
      
      // Reset shadow for next ray
      ctx.shadowBlur = 0;
    });
    
    // Reset composite operation
    ctx.globalCompositeOperation = 'source-over';
  };

  const animate = () => {
    if (!canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    if (!ctx) return;
    
    updateRays(canvas.width, canvas.height);
    drawRays(ctx);
    
    animationRef.current = requestAnimationFrame(animate);
  };

  const handleResize = () => {
    if (!canvasRef.current) return;
    
    const canvas = canvasRef.current;
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    
    initRays(canvas.width, canvas.height);
  };

  useEffect(() => {
    if (!canvasRef.current) return;
    
    const canvas = canvasRef.current;
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    
    initRays(canvas.width, canvas.height);
    animate();
    
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 0,
        opacity: 0.7,
        pointerEvents: 'none', // Make sure it doesn't interfere with clicks
      }}
    >
      <canvas
        ref={canvasRef}
        style={{
          display: 'block',
          width: '100%',
          height: '100%',
        }}
      />
    </Box>
  );
};

export default AnimatedBackground;
